import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CreateLocationParams,
  CreateLocationResponseSchema,
  GetLocationResponseSchema,
  ListLocationsResponseSchema,
  Location,
  UpdateLocationParams,
  UpdateLocationResponseSchema,
} from "@forgepoint/shared";

interface LocationLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface LocationsStore {
  locations: Map<string, Location>;
  locationLoadingStates: Map<string, LocationLoadingState>;
  listLoading: boolean;
  listError: string | null;

  getLocation: (id: string) => Location | undefined;
  getLocationLoadingState: (id: string) => LocationLoadingState;
  fetchLocations: (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }) => Promise<{ ids: string[]; total: number }>;
  fetchLocation: (id: string) => Promise<void>;
  createLocation: (
    data: CreateLocationParams,
  ) => Promise<{ location: Location | null; error: string | null }>;
  updateLocation: (
    id: string,
    data: UpdateLocationParams,
  ) => Promise<{ location: Location | null; error: string | null }>;
  deleteLocation: (id: string) => Promise<{ success: boolean; error?: string }>;
  mergeLocations: (locations: Location[]) => void;
}

const getInitialLoadingState = (): LocationLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

export const useLocationsStore = create<LocationsStore>((set, get) => ({
  locations: new Map(),
  locationLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  getLocation: (id) => get().locations.get(id),
  getLocationLoadingState: (id) =>
    get().locationLoadingStates.get(id) || getInitialLoadingState(),
  mergeLocations: (locationsToMerge) => {
    if (locationsToMerge.length === 0) {
      return;
    }
    set((state) => {
      const locations = new Map(state.locations);
      locationsToMerge.forEach((location) => {
        locations.set(location.id, location);
      });
      return { locations };
    });
  },

  fetchLocations: async (params) => {
    set({ listLoading: true, listError: null });

    try {
      const searchParams = new URLSearchParams();
      if (params?.search) {
        searchParams.set("search", params.search);
      }
      if (params?.limit !== undefined) {
        searchParams.set("limit", String(params.limit));
      }
      if (params?.offset !== undefined) {
        searchParams.set("offset", String(params.offset));
      }

      const queryString = searchParams.toString();
      const response = await fetchApi(
        queryString ? `locations?${queryString}` : "locations",
        "GET",
      );

      if (response.ok && response.data) {
        const data = ListLocationsResponseSchema.parse(response.data);
        set((state) => {
          const locations = new Map(state.locations);
          data.locations.forEach((location) => {
            locations.set(location.id, location);
          });
          return { locations, listLoading: false };
        });
        return {
          ids: data.locations.map((location) => location.id),
          total: data.total,
        };
      }

      set({
        listLoading: false,
        listError: response.error ?? "Failed to fetch locations",
      });
      return { ids: [], total: 0 };
    } catch (error) {
      captureException(error as Error);
      set({ listLoading: false, listError: "Failed to fetch locations" });
      return { ids: [], total: 0 };
    }
  },

  fetchLocation: async (id) => {
    set((state) => {
      const locationLoadingStates = new Map(state.locationLoadingStates);
      const currentState =
        locationLoadingStates.get(id) || getInitialLoadingState();
      locationLoadingStates.set(id, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { locationLoadingStates };
    });

    try {
      const response = await fetchApi(`locations/${id}`, "GET");
      if (response.ok && response.data) {
        const data = GetLocationResponseSchema.parse(response.data);
        set((state) => {
          const locations = new Map(state.locations);
          const locationLoadingStates = new Map(state.locationLoadingStates);
          const currentState =
            locationLoadingStates.get(id) || getInitialLoadingState();
          locations.set(data.location.id, data.location);
          locationLoadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return { locations, locationLoadingStates };
        });
        return;
      }

      set((state) => {
        const locationLoadingStates = new Map(state.locationLoadingStates);
        const currentState =
          locationLoadingStates.get(id) || getInitialLoadingState();
        locationLoadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: response.error ?? "Failed to fetch location",
        });
        return { locationLoadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const locationLoadingStates = new Map(state.locationLoadingStates);
        const currentState =
          locationLoadingStates.get(id) || getInitialLoadingState();
        locationLoadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch location",
        });
        return { locationLoadingStates };
      });
    }
  },

  createLocation: async (data) => {
    try {
      const response = await fetchApi("locations", "POST", { body: data });
      if (response.ok && response.data) {
        const result = CreateLocationResponseSchema.parse(response.data);
        set((state) => {
          const locations = new Map(state.locations);
          locations.set(result.location.id, result.location);
          return { locations };
        });
        return { location: result.location, error: null };
      }
      return {
        location: null,
        error: response.error ?? "Failed to create location",
      };
    } catch (error) {
      captureException(error as Error);
      return { location: null, error: "Failed to create location" };
    }
  },

  updateLocation: async (id, data) => {
    set((state) => {
      const locationLoadingStates = new Map(state.locationLoadingStates);
      const currentState =
        locationLoadingStates.get(id) || getInitialLoadingState();
      locationLoadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { locationLoadingStates };
    });

    try {
      const response = await fetchApi(`locations/${id}`, "PUT", {
        body: data,
      });
      if (response.ok && response.data) {
        const result = UpdateLocationResponseSchema.parse(response.data);
        set((state) => {
          const locations = new Map(state.locations);
          const locationLoadingStates = new Map(state.locationLoadingStates);
          const currentState =
            locationLoadingStates.get(id) || getInitialLoadingState();
          locations.set(result.location.id, result.location);
          locationLoadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { locations, locationLoadingStates };
        });
        return { location: result.location, error: null };
      }

      const error = response.error ?? "Failed to update location";
      set((state) => {
        const locationLoadingStates = new Map(state.locationLoadingStates);
        const currentState =
          locationLoadingStates.get(id) || getInitialLoadingState();
        locationLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: error,
        });
        return { locationLoadingStates };
      });
      return { location: null, error };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const locationLoadingStates = new Map(state.locationLoadingStates);
        const currentState =
          locationLoadingStates.get(id) || getInitialLoadingState();
        locationLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update location",
        });
        return { locationLoadingStates };
      });
      return { location: null, error: "Failed to update location" };
    }
  },

  deleteLocation: async (id) => {
    set((state) => {
      const locationLoadingStates = new Map(state.locationLoadingStates);
      const currentState =
        locationLoadingStates.get(id) || getInitialLoadingState();
      locationLoadingStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { locationLoadingStates };
    });

    try {
      const response = await fetchApi(`locations/${id}`, "DELETE");
      if (response.ok) {
        set((state) => {
          const locations = new Map(state.locations);
          const locationLoadingStates = new Map(state.locationLoadingStates);
          locations.delete(id);
          locationLoadingStates.delete(id);
          return { locations, locationLoadingStates };
        });
        return { success: true };
      }

      const error = response.error ?? "Failed to delete location";
      set((state) => {
        const locationLoadingStates = new Map(state.locationLoadingStates);
        const currentState =
          locationLoadingStates.get(id) || getInitialLoadingState();
        locationLoadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: error,
        });
        return { locationLoadingStates };
      });
      return { success: false, error };
    } catch (error) {
      captureException(error as Error);
      const errorMessage = "Failed to delete location";
      set((state) => {
        const locationLoadingStates = new Map(state.locationLoadingStates);
        const currentState =
          locationLoadingStates.get(id) || getInitialLoadingState();
        locationLoadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        });
        return { locationLoadingStates };
      });
      return { success: false, error: errorMessage };
    }
  },
}));
